/**
 * 見学予約フォーム（#visit-reservation-form）のクライアント側バリデーション
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';

import { isValidEmail } from './email-validation.js';

const ZIP_RE = /^\d{3}-?\d{4}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
}

export function clearVisitFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.type === 'checkbox' && control.id === 'visit-privacy') {
		const consent = control.closest('.visit__consent');
		if (consent) {
			consent.classList.add('is-invalid');
			consent.appendChild(mountErrorParagraph(controlId, message));
		}
		return;
	}

	if (control.id === 'visit-name-family' || control.id === 'visit-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	const schedule = el.closest('.visit__schedule');
	if (schedule instanceof HTMLElement) {
		const focusable = schedule.querySelector('.visit__dateCard:not([disabled]), .visit__timeSlot[data-value]');
		if (focusable instanceof HTMLElement) {
			focusable.focus();
			return;
		}
	}
	const fpRoot = el.closest('.flatpickr');
	if (fpRoot) {
		const alt = fpRoot.querySelector('.flatpickr-alt-input');
		(alt || el).focus();
		return;
	}
	el.focus();
}

/**
 * @returns {boolean}
 */
export function validateVisitReservationForm(form) {
	clearVisitFormErrors(form);

	const errors = [];

	const d1 = trimValue('visit-date-1');
	if (!d1) {
		errors.push(['visit-date-1', '見学希望日時を選択してください。']);
	}

	const family = trimValue('visit-name-family');
	const given = trimValue('visit-name-given');
	if (!family && !given) {
		errors.push(['visit-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['visit-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['visit-name-given', '名を入力してください。']);
	}

	const telRaw = trimValue('visit-tel');
	if (!telRaw) {
		errors.push(['visit-tel', '電話番号を入力してください。']);
	} else if (!isValidPhoneTel(telRaw)) {
		errors.push(['visit-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	const email = trimValue('visit-email');
	if (!email) {
		errors.push(['visit-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['visit-email', 'メールアドレスの形式が正しくありません。']);
	}

	const privacy = form.querySelector('#visit-privacy');
	if (!privacy || !privacy.checked) {
		errors.push(['visit-privacy', 'プライバシーポリシーに同意してください。']);
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initVisitFormValidation(form) {
	if (!form || form.id !== 'visit-reservation-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !t.id) return;
		if (form.contains(t)) {
			clearErrorForControl(form, t.id);
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
